// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';

const logger = new Logger('AutostartUtils');

let cachedAutostartEnabled: boolean | null = null;

export function getCachedAutostartStatus(): boolean | null {
	return cachedAutostartEnabled;
}

export async function setAutostartEnabled(enabled: boolean): Promise<boolean | null> {
	const electronApi = getElectronAPI();
	if (!electronApi) return null;
	try {
		if (enabled) {
			await electronApi.autostartEnable();
		} else {
			await electronApi.autostartDisable();
		}
		const next = await electronApi.autostartIsEnabled();
		cachedAutostartEnabled = next;
		return next;
	} catch (error) {
		logger.error('Failed to update autostart status', error);
		return null;
	}
}

export async function getAutostartStatus(): Promise<boolean | null> {
	const electronApi = getElectronAPI();
	if (!electronApi) return null;
	try {
		const enabled = await electronApi.autostartIsEnabled();
		cachedAutostartEnabled = enabled;
		return enabled;
	} catch (error) {
		logger.error('Failed to read autostart status', error);
		return null;
	}
}

// Launch-at-login is opt-in: the first run only records the current state so the
// settings toggle shows the truth. It never turns autostart on by itself.
export async function initializeAutostart(): Promise<boolean | null> {
	const electronApi = getElectronAPI();
	if (!electronApi) return null;
	try {
		const enabled = await electronApi.autostartIsEnabled();
		const initialized = await electronApi.autostartIsInitialized();
		if (!initialized) {
			await electronApi.autostartMarkInitialized();
		}
		cachedAutostartEnabled = enabled;
		return enabled;
	} catch (error) {
		logger.error('Failed to initialize autostart status', error);
		return null;
	}
}
